
import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase/client';
import {
  X, Save, Loader2, Search, Building2, Users, Calendar, Euro
} from 'lucide-react';
import type { Vertrag } from '../../../lib/vertraege/typ';
import { hausAdresse, sortiereHaeuser, type Haus } from '../../../lib/objekte/haeuser';
import {
  Buildings, CalendarBlank, Car, CurrencyEur, FileText as PhFileText, FloppyDisk, HouseLine, Infinity as PhInfinity, LinkSimple,
  Minus, NotePencil, Plus as PhPlus, Repeat, Storefront, User,
} from '@phosphor-icons/react';
import { FormRahmen } from '../../../components/island/form/FormRahmen';
import { FormAbschnitt } from '../../../components/island/form/FormAbschnitt';
import { FormFeld, FormZeile } from '../../../components/island/form/FormFeld';
import { FormSegment } from '../../../components/island/form/FormSegment';
import { FormAuswahl } from '../../../components/island/form/FormAuswahl';
import { FormSuche } from '../../../components/island/form/FormSuche';
import { FormSchalter } from '../../../components/island/form/FormSchalter';
import { FormKnopfleiste } from '../../../components/island/form/FormKnopfleiste';
import { useFormFehler } from '../../../components/island/form/useFormFehler';
import { ABSCHNITT_FARBE, CHIP, ZEILEN_ICON } from '../../../components/island/form/form-stil';

interface VertragFormularProps {
  editVertrag: Vertrag | null;
  userProfile?: any;
  onClose: () => void;
  onSaved: () => void;
  /**
   * 'pille' rendert ohne Overlay/eigenen Header für den Island-Formularbereich
   * (dunkles Theme — die Pille liefert Titelzeile + Schließen), wie TerminFormular.
   */
  /** 'immo' = neues Design (Mieter-/Immobilienbereich): Design-Tokens, inline statt Pille/Modal. */
  variant?: 'modal' | 'pille' | 'immo';
  /** Vorbelegung beim Anlegen aus der Mieterseite (Mieter, Objekt, Einheit) */
  prefillMieterId?: string | null;
  prefillObjektId?: string | null;
  prefillEinheit?: string | null;
}

const VertragFormular: React.FC<VertragFormularProps> = ({
  editVertrag, userProfile, onClose, onSaved, variant = 'modal', prefillMieterId, prefillObjektId, prefillEinheit,
}) => {
  const isEdit = !!editVertrag;
  const pille = variant === 'pille';

  const immo = variant === 'immo';
  /** Klassen je Variante: Pille (dunkel), Modal (hell), immo (neues Design mit Tokens). */
  const w = (pilleCls: string, modalCls: string, immoCls: string) => (immo ? immoCls : pille ? pilleCls : modalCls);
  const IMMO_FELD = 'w-full min-h-[50px] px-[14px] py-3 rounded-[15px] bg-[var(--bg)] text-[15px] font-bold text-[var(--ink)] placeholder:text-[var(--ink3)] outline-none shadow-[inset_0_0_0_1px_var(--line)] focus:shadow-[inset_0_0_0_2px_var(--acc)] transition-all';
  const labelBasisCls = w(
    'text-[9px] font-black text-white/40 uppercase tracking-[0.2em]',
    'text-[9px] font-black text-gray-400 uppercase tracking-[0.2em]',
    'text-[12px] font-bold text-[var(--ink3)]');
  const labelCls = `${labelBasisCls} mb-1.5 block`;
  const inputCls = w(
    'w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm font-semibold text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[#c4a77d]/40 focus:border-[#c4a77d]/40 transition-all [color-scheme:dark]',
    'w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-sm font-semibold text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#c4a77d]/30 focus:border-[#c4a77d] transition-all',
    IMMO_FELD);
  const searchInputCls = w(
    'w-full pl-9 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm font-semibold text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[#c4a77d]/40 transition-all',
    'w-full pl-9 pr-4 py-3 rounded-xl border border-gray-200 text-sm font-semibold text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#c4a77d]/30 focus:border-[#c4a77d] transition-all',
    `${IMMO_FELD} pl-9`);
  const searchIconCls = w('absolute left-3 top-1/2 -translate-y-1/2 text-white/30', 'absolute left-3 top-1/2 -translate-y-1/2 text-gray-300', 'absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink3)]');
  const chipCls = w(
    'flex items-center justify-between px-4 py-3 bg-white/5 rounded-xl border border-[#c4a77d]/40',
    'flex items-center justify-between px-4 py-3 bg-gray-50 rounded-xl border border-gray-200',
    'flex items-center justify-between min-h-[50px] px-[14px] rounded-[15px] bg-[var(--fill2)]');
  const chipTextCls = w('text-sm font-semibold text-white', 'text-sm font-semibold text-gray-800', 'text-[15px] font-bold text-[var(--ink)]');
  const entfernenCls = w('text-white/30 hover:text-red-400', 'text-gray-400 hover:text-red-400', 'text-[var(--ink3)] hover:text-[var(--bad)]');
  const smallSelectCls = w(
    'w-full px-3 py-3 rounded-xl bg-white/5 border border-white/10 text-[11px] font-semibold text-white focus:outline-none focus:ring-2 focus:ring-[#c4a77d]/40',
    'w-full px-3 py-3 rounded-xl border border-gray-200 text-[11px] font-semibold text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-[#c4a77d]/30',
    IMMO_FELD);
  const zahlCls = w(
    'w-16 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm font-bold text-white text-center focus:outline-none focus:ring-2 focus:ring-[#c4a77d]/40',
    'w-16 px-3 py-2 rounded-lg border border-gray-200 text-sm font-bold text-gray-800 text-center focus:outline-none focus:ring-2 focus:ring-[#c4a77d]/30',
    'w-16 h-10 px-2 rounded-[12px] bg-[var(--bg)] text-[15px] font-bold text-[var(--ink)] text-center outline-none shadow-[inset_0_0_0_1px_var(--line)] focus:shadow-[inset_0_0_0_2px_var(--acc)]');
  // immo: Farbe erbt vom Farbschema (hell/dunkel), sonst wären Optionen im dunklen Menü unlesbar
  const optionCls = immo ? '' : 'text-gray-900';
  const dropdownPanelCls = immo
    ? 'absolute top-full mt-1 left-0 right-0 rounded-[16px] bg-[var(--bg)] shadow-[0_0_0_1px_var(--line),0_16px_30px_-14px_var(--shade)] z-10 overflow-hidden'
    : 'absolute top-full mt-1 left-0 right-0 bg-white rounded-xl border border-gray-200 shadow-xl z-10 overflow-hidden';
  const dropdownItemCls = immo
    ? 'w-full px-4 py-2.5 text-left hover:bg-[var(--hover)] text-sm font-semibold text-[var(--ink)]'
    : 'w-full px-4 py-2.5 text-left hover:bg-gray-50 text-sm font-semibold text-gray-700 border-b border-gray-50 last:border-0';
  const nebenCls = immo ? 'text-[var(--ink3)] text-xs' : 'text-gray-400 text-xs';
  const schalterTextCls = w('text-[10px] font-black text-gray-500 uppercase tracking-widest', 'text-[10px] font-black text-gray-500 uppercase tracking-widest', 'text-[13px] font-bold text-[var(--ink)]');
  const kleinLabelCls = w('text-[9px] font-black text-gray-400 uppercase tracking-widest', 'text-[9px] font-black text-gray-400 uppercase tracking-widest', 'text-[12px] font-bold text-[var(--ink3)]');

  const [vertragsnummer, setVertragsnummer] = useState(editVertrag?.vertragsnummer || '');
  const [vertragsart, setVertragsart] = useState(editVertrag?.vertragsart || 'wohnung');
  const [beginn, setBeginn] = useState(editVertrag?.beginn || '');
  const [ende, setEnde] = useState(editVertrag?.ende || '');
  const [unbefristet, setUnbefristet] = useState(editVertrag?.unbefristet ?? false);
  const [kuendigungsfristMonate, setKuendigungsfristMonate] = useState(editVertrag?.kuendigungsfrist_monate ?? 3);
  const [kaltmiete, setKaltmiete] = useState(editVertrag?.kaltmiete?.toString() || '');
  const [nebenkosten, setNebenkosten] = useState(editVertrag?.nebenkosten?.toString() || '');
  const [kautionHoehe, setKautionHoehe] = useState(editVertrag?.kaution_hoehe?.toString() || '');
  const [kautionStatus, setKautionStatus] = useState(editVertrag?.kaution_status || 'offen');
  const [zahlungsweise, setZahlungsweise] = useState(editVertrag?.zahlungsweise || 'monatlich');
  const [mietanpassungTyp, setMietanpassungTyp] = useState(editVertrag?.mietanpassung_typ || 'keine');
  const [status, setStatus] = useState(editVertrag?.status || 'entwurf');
  const [einheit, setEinheit] = useState(editVertrag?.einheit || prefillEinheit || '');
  const [notizen, setNotizen] = useState(editVertrag?.notizen || '');

  const [mieterId, setMieterId] = useState(editVertrag?.mieter_id || prefillMieterId || '');
  const [objektId, setObjektId] = useState(editVertrag?.objekt_id || prefillObjektId || '');
  // Optionale Haus-Ebene des gewählten Objekts (leer = kein Feld)
  const [haeuser, setHaeuser] = useState<Haus[]>([]);
  const [hausId, setHausId] = useState<string>(editVertrag?.haus_id || '');
  const [selectedMieter, setSelectedMieter] = useState<any>(editVertrag?.mieter || null);
  const [selectedObjekt, setSelectedObjekt] = useState<any>(editVertrag?.objekt || null);
  const [mieterSearch, setMieterSearch] = useState('');
  const [objektSearch, setObjektSearch] = useState('');
  const [mieterResults, setMieterResults] = useState<any[]>([]);
  const [objektResults, setObjektResults] = useState<any[]>([]);

  const [saving, setSaving] = useState(false);
  // Pille: Meldungen in der Fehlerleiste statt alert (Modal/immo unverändert mit alert).
  const fehlerPille = useFormFehler();
  const melde = (text: string) => { if (pille) fehlerPille.zeige(text); else alert(text); };

  // Vorbelegte Verknüpfungen (Mieter, Objekt) als Auswahl anzeigen
  useEffect(() => {
    if (!mieterId || selectedMieter) return;
    let aktiv = true;
    supabase.from('mieter').select('id, vorname, nachname').eq('id', mieterId).maybeSingle()
      .then(({ data }) => { if (aktiv && data) setSelectedMieter(data); });
    return () => { aktiv = false; };
  }, [mieterId, selectedMieter]);
  useEffect(() => {
    if (!objektId || selectedObjekt) return;
    let aktiv = true;
    supabase.from('objekte').select('id, bezeichnung, strasse, hausnummer').eq('id', objektId).maybeSingle()
      .then(({ data }) => { if (aktiv && data) setSelectedObjekt(data); });
    return () => { aktiv = false; };
  }, [objektId, selectedObjekt]);

  // Auto-generate vertragsnummer
  useEffect(() => {
    if (!isEdit && !vertragsnummer) {
      const prefix = vertragsart === 'wohnung' ? 'MV' : vertragsart === 'gewerbe' ? 'GV' : 'SV';
      const year = new Date().getFullYear();
      const rand = String(Math.floor(Math.random() * 900) + 100);
      setVertragsnummer(`${prefix}-${year}-${rand}`);
    }
  }, [vertragsart]);

  // Typeahead
  useEffect(() => {
    if (mieterSearch.length < 2) { setMieterResults([]); return; }
    const t = setTimeout(async () => {
      const { data } = await supabase.from('mieter').select('id, vorname, nachname').or(`vorname.ilike.%${mieterSearch}%,nachname.ilike.%${mieterSearch}%`).limit(5);
      setMieterResults(data || []);
    }, 300);
    return () => clearTimeout(t);
  }, [mieterSearch]);

  useEffect(() => {
    if (objektSearch.length < 2) { setObjektResults([]); return; }
    const t = setTimeout(async () => {
      const { data } = await supabase.from('objekte').select('id, bezeichnung, strasse, hausnummer').ilike('bezeichnung', `%${objektSearch}%`).limit(5);
      setObjektResults(data || []);
    }, 300);
    return () => clearTimeout(t);
  }, [objektSearch]);

  useEffect(() => {
    if (!objektId) { setHaeuser([]); return; }
    let aktiv = true;
    supabase.from('objekt_haeuser').select('*').eq('objekt_id', objektId).then(({ data }) => {
      if (aktiv) setHaeuser(sortiereHaeuser((data as Haus[]) || []));
    });
    return () => { aktiv = false; };
  }, [objektId]);

  const handleSave = async () => {
    if (!vertragsnummer.trim() || !beginn) return;
    setSaving(true);
    fehlerPille.leeren();

    try {
      const payload: any = {
        vertragsnummer: vertragsnummer.trim(),
        vertragsart,
        mieter_id: mieterId || null,
        objekt_id: objektId || null,
        // Haus nur speichern, wenn es zum gewählten Objekt gehört (DB-Trigger prüft das ebenfalls)
        haus_id: hausId && haeuser.some(h => h.id === hausId) ? hausId : null,
        einheit: einheit.trim() || null,
        beginn,
        ende: unbefristet ? null : (ende || null),
        unbefristet,
        kuendigungsfrist_monate: kuendigungsfristMonate,
        kaltmiete: parseFloat(kaltmiete) || 0,
        nebenkosten: parseFloat(nebenkosten) || 0,
        kaution_hoehe: parseFloat(kautionHoehe) || 0,
        kaution_status: kautionStatus,
        zahlungsweise,
        mietanpassung_typ: mietanpassungTyp,
        status,
        notizen: notizen.trim() || null,
        updated_at: new Date().toISOString(),
      };

      if (isEdit) {
        const { error } = await supabase.from('vertraege').update(payload).eq('id', editVertrag!.id);
        if (error) { console.error('Update error:', error); melde(`Fehler: ${error.message}`); return; }
      } else {
        payload.created_by = userProfile?.id;
        const { error } = await supabase.from('vertraege').insert(payload);
        if (error) { console.error('Insert error:', error); melde(`Fehler: ${error.message}`); return; }

        // Kalender-Erinnerungen erstellen
        if (status === 'aktiv') {
          await createCalendarReminders(payload);
        }
      }

      onSaved();
    } catch (err: any) {
      console.error('Fehler:', err);
      melde(`Fehler: ${err?.message || err}`);
    } finally {
      setSaving(false);
    }
  };

  const createCalendarReminders = async (vertrag: any) => {
    const termine: any[] = [];

    // Vertragsende Erinnerung
    if (vertrag.ende) {
      termine.push({
        titel: `Vertragsende: ${vertrag.vertragsnummer}`,
        typ: 'frist',
        start_datum: `${vertrag.ende}T09:00:00`,
        ganztaegig: true,
        status: 'geplant',
        objekt_id: vertrag.objekt_id,
        mieter_id: vertrag.mieter_id,
        created_by: userProfile?.id,
        beschreibung: `Vertrag ${vertrag.vertragsnummer} läuft aus.`,
      });

      // Kündigungsfrist Erinnerung
      if (vertrag.kuendigungsfrist_monate > 0) {
        const endDate = new Date(vertrag.ende);
        endDate.setMonth(endDate.getMonth() - vertrag.kuendigungsfrist_monate);
        const reminderDate = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;
        termine.push({
          titel: `Kündigungsfrist: ${vertrag.vertragsnummer}`,
          typ: 'frist',
          start_datum: `${reminderDate}T09:00:00`,
          ganztaegig: true,
          status: 'geplant',
          objekt_id: vertrag.objekt_id,
          mieter_id: vertrag.mieter_id,
          created_by: userProfile?.id,
          beschreibung: `Kündigungsfrist für Vertrag ${vertrag.vertragsnummer} endet in ${vertrag.kuendigungsfrist_monate} Monaten.`,
        });
      }
    }

    if (termine.length > 0) {
      await supabase.from('termine').insert(termine);
    }
  };

  const inhalt = (
        <div className={immo ? 'space-y-5' : pille ? 'p-4 space-y-5' : 'flex-1 overflow-y-auto p-6 space-y-5 custom-scrollbar'}>
          {/* Vertragsnummer + Art */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Vertragsnummer *</label>
              <input type="text" value={vertragsnummer} onChange={(e) => setVertragsnummer(e.target.value)}
                className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Vertragsart</label>
              <select value={vertragsart} onChange={(e) => setVertragsart(e.target.value)}
                className={inputCls}>
                <option className={optionCls} value="wohnung">🏠 Wohnung</option>
                <option className={optionCls} value="gewerbe">🏢 Gewerbe</option>
                <option className={optionCls} value="stellplatz">🅿️ Stellplatz</option>
              </select>
            </div>
          </div>

          {/* Status */}
          <div>
            <label className={labelCls}>Status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)}
              className={inputCls}>
              <option className={optionCls} value="entwurf">Entwurf</option>
              <option className={optionCls} value="aktiv">Aktiv</option>
              <option className={optionCls} value="gekuendigt">Gekündigt</option>
              <option className={optionCls} value="ausgelaufen">Ausgelaufen</option>
            </select>
          </div>

          {/* Objekt Typeahead */}
          <div>
            <label className={`${labelBasisCls} mb-1.5 flex items-center gap-1.5`}><Building2 size={10} /> Objekt</label>
            {selectedObjekt ? (
              <div className={chipCls}>
                <span className={chipTextCls}>{selectedObjekt.bezeichnung}</span>
                <button onClick={() => { setSelectedObjekt(null); setObjektId(''); setHausId(''); }} className={entfernenCls}><X size={14} /></button>
              </div>
            ) : (
              <div className="relative">
                <div className={searchIconCls}><Search size={14} /></div>
                <input type="text" value={objektSearch} onChange={(e) => setObjektSearch(e.target.value)} placeholder="Objekt suchen..."
                  className={searchInputCls} />
                {objektResults.length > 0 && (
                  <div className={dropdownPanelCls}>
                    {objektResults.map(o => (
                      <button key={o.id} onClick={() => { setObjektId(o.id); setSelectedObjekt(o); setObjektSearch(''); setObjektResults([]); setHausId(''); }}
                        className={dropdownItemCls}>
                        {o.bezeichnung} <span className={nebenCls}>— {o.strasse} {o.hausnummer}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Haus — nur bei Liegenschaften mit mehreren Häusern */}
          {haeuser.length > 0 && (
            <div>
              <label className={labelCls}>Haus</label>
              <select value={hausId} onChange={(e) => setHausId(e.target.value)} className={inputCls}>
                <option value="">Ohne Haus (ganze Liegenschaft)</option>
                {haeuser.map(h => (
                  <option key={h.id} value={h.id}>
                    {hausAdresse(h) ? `${h.bezeichnung} · ${hausAdresse(h)}` : h.bezeichnung}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Einheit */}
          <div>
            <label className={labelCls}>Einheit / Wohnung</label>
            <input type="text" value={einheit} onChange={(e) => setEinheit(e.target.value)} placeholder="z.B. EG links, Stellplatz 3"
              className={inputCls} />
          </div>

          {/* Mieter Typeahead */}
          <div>
            <label className={`${labelBasisCls} mb-1.5 flex items-center gap-1.5`}><Users size={10} /> Mieter</label>
            {selectedMieter ? (
              <div className={chipCls}>
                <span className={chipTextCls}>{selectedMieter.vorname} {selectedMieter.nachname}</span>
                <button onClick={() => { setSelectedMieter(null); setMieterId(''); }} className={entfernenCls}><X size={14} /></button>
              </div>
            ) : (
              <div className="relative">
                <div className={searchIconCls}><Search size={14} /></div>
                <input type="text" value={mieterSearch} onChange={(e) => setMieterSearch(e.target.value)} placeholder="Mieter suchen..."
                  className={searchInputCls} />
                {mieterResults.length > 0 && (
                  <div className={dropdownPanelCls}>
                    {mieterResults.map(m => (
                      <button key={m.id} onClick={() => { setMieterId(m.id); setSelectedMieter(m); setMieterSearch(''); setMieterResults([]); }}
                        className={dropdownItemCls}>
                        {m.vorname} {m.nachname}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Laufzeit */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className={`${labelBasisCls} mb-1.5 flex items-center gap-1.5`}><Calendar size={10} /> Beginn *</label>
              <input type="date" value={beginn} onChange={(e) => setBeginn(e.target.value)}
                className={inputCls} />
            </div>
            {!unbefristet && (
              <div>
                <label className={`${labelBasisCls} mb-1.5 flex items-center gap-1.5`}><Calendar size={10} /> Ende</label>
                <input type="date" value={ende} onChange={(e) => setEnde(e.target.value)}
                  className={inputCls} />
              </div>
            )}
          </div>

          {/* Unbefristet + Kündigungsfrist */}
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-3 cursor-pointer">
              <div className={`w-10 h-5 rounded-full transition-all ${unbefristet ? (immo ? 'bg-[var(--ok)]' : 'bg-[#c4a77d]') : w('bg-gray-200', 'bg-gray-200', 'bg-[var(--fill3)]')}`} onClick={() => setUnbefristet(!unbefristet)}>
                <div className={`w-5 h-5 rounded-full bg-white shadow-md transform transition-transform ${unbefristet ? 'translate-x-5' : 'translate-x-0'}`} />
              </div>
              <span className={schalterTextCls}>Unbefristet</span>
            </label>
            <div className="flex items-center gap-2">
              <label className={kleinLabelCls}>Kündigungsfrist</label>
              <input type="number" value={kuendigungsfristMonate} onChange={(e) => setKuendigungsfristMonate(parseInt(e.target.value) || 0)} min={0} max={24}
                className={zahlCls} />
              <span className={kleinLabelCls}>Mon.</span>
            </div>
          </div>

          {/* Finanzen */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className={`${labelBasisCls} mb-1.5 flex items-center gap-1.5`}><Euro size={10} /> Kaltmiete</label>
              <input type="number" value={kaltmiete} onChange={(e) => setKaltmiete(e.target.value)} step="0.01" placeholder="0.00"
                className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Nebenkosten</label>
              <input type="number" value={nebenkosten} onChange={(e) => setNebenkosten(e.target.value)} step="0.01" placeholder="0.00"
                className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Kaution</label>
              <input type="number" value={kautionHoehe} onChange={(e) => setKautionHoehe(e.target.value)} step="0.01" placeholder="0.00"
                className={inputCls} />
            </div>
          </div>

          {/* Kaution Status + Zahlungsweise + Mietanpassung */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className={labelCls}>Kaution Status</label>
              <select value={kautionStatus} onChange={(e) => setKautionStatus(e.target.value)}
                className={smallSelectCls}>
                <option className={optionCls} value="offen">Offen</option>
                <option className={optionCls} value="hinterlegt">Hinterlegt</option>
                <option className={optionCls} value="zurueckgezahlt">Zurückgezahlt</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Zahlungsweise</label>
              <select value={zahlungsweise} onChange={(e) => setZahlungsweise(e.target.value)}
                className={smallSelectCls}>
                <option className={optionCls} value="monatlich">Monatlich</option>
                <option className={optionCls} value="quartalsweise">Quartalsweise</option>
                <option className={optionCls} value="halbjaehrlich">Halbjährlich</option>
                <option className={optionCls} value="jaehrlich">Jährlich</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Mietanpassung</label>
              <select value={mietanpassungTyp} onChange={(e) => setMietanpassungTyp(e.target.value)}
                className={smallSelectCls}>
                <option className={optionCls} value="keine">Keine</option>
                <option className={optionCls} value="staffel">Staffelmiete</option>
                <option className={optionCls} value="index">Indexmiete</option>
              </select>
            </div>
          </div>

          {/* Notizen */}
          <div>
            <label className={labelCls}>Notizen</label>
            <textarea value={notizen} onChange={(e) => setNotizen(e.target.value)} rows={3} placeholder="Optionale Notizen..."
              className={`${inputCls} resize-none`} />
          </div>

          {/* Aktionen (nur Pille — im Modal sitzt der Footer außerhalb) */}
          {(pille || immo) && (
            <div className={`flex items-center ${immo ? 'justify-end gap-2' : 'justify-between'} pt-2`}>
              <button onClick={onClose} className={immo
                ? 'h-11 px-5 rounded-[22px] bg-[var(--fill2)] text-[13px] font-bold text-[var(--ink)] hover:bg-[var(--hover)] transition-colors'
                : 'px-5 py-3 text-[10px] font-black text-white/40 uppercase tracking-widest hover:text-white transition-colors'}>
                Abbrechen
              </button>
              <button onClick={handleSave} disabled={saving || !vertragsnummer.trim() || !beginn}
                className={immo
                  ? 'flex items-center gap-2 h-11 px-6 rounded-[22px] [background:var(--acc-fill)] text-[var(--acc-ink)] text-[13px] font-extrabold shadow-[var(--acc-sh)] transition-all active:scale-95 disabled:opacity-45 disabled:cursor-not-allowed'
                  : 'flex items-center gap-2 px-6 py-3 bg-[#c4a77d] text-white rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg hover:bg-[#b3966c] transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed'}>
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                {isEdit ? 'Speichern' : 'Erstellen'}
              </button>
            </div>
          )}
        </div>
  );

  // Pille (Handoff Pillen-Formulare §11): Bausteine, gleiche Zustände und Handler wie oben.
  if (pille) {
    const neutral = { iconFlaeche: ZEILEN_ICON.gedaempft.flaeche, iconFarbe: ZEILEN_ICON.gedaempft.farbe };
    const dunkel = ZEILEN_ICON.dunkel.flaeche;
    // Emojis der alten Auswahl (🏠 🏢 🅿️) durch Icons ersetzt
    const ARTEN = [
      { wert: 'wohnung', label: 'Wohnung', icon: HouseLine, iconFarbe: ZEILEN_ICON.blau.farbe, iconFlaeche: dunkel },
      { wert: 'gewerbe', label: 'Gewerbe', icon: Storefront, iconFarbe: CHIP.orange.text, iconFlaeche: dunkel },
      { wert: 'stellplatz', label: 'Stellplatz', icon: Car, iconFarbe: ZEILEN_ICON.violett.farbe, iconFlaeche: dunkel },
    ];
    return (
      <FormRahmen fehler={fehlerPille.text} knopfleiste={
        <FormKnopfleiste abbrechen={{ label: 'Abbrechen', onClick: onClose }}
          haupt={{
            label: isEdit ? 'Speichern' : 'Erstellen', icon: isEdit ? FloppyDisk : PhPlus, typ: 'button', onClick: handleSave, laedt: saving,
            gesperrt: (!vertragsnummer.trim() || !beginn) && !saving, gesperrtGrund: 'Vertragsnummer und Beginn eingeben',
          }} />
      }>
        <FormAbschnitt titel="Vertrag" icon={PhFileText} farbe="blau" pflicht={1} index={0}>
          <FormZeile>
            <FormFeld label="Vertragsnummer" value={vertragsnummer} onChange={(e) => setVertragsnummer(e.target.value)} pflicht
              tip="Vertragsnummer – Wird beim Anlegen automatisch vorgeschlagen" />
            <FormAuswahl label="Vertragsart" wert={vertragsart} onChange={setVertragsart} optionen={ARTEN} />
          </FormZeile>
          <FormSegment label="Status" wert={status} onChange={setStatus} optionen={[
            { wert: 'entwurf', label: 'Entwurf' }, { wert: 'aktiv', label: 'Aktiv' },
            { wert: 'gekuendigt', label: 'Gekündigt' }, { wert: 'ausgelaufen', label: 'Ausgelaufen' },
          ]} />
        </FormAbschnitt>

        <FormAbschnitt titel="Zuordnung" icon={LinkSimple} farbe="gold" index={1}>
          <FormSuche label="Objekt" platzhalter="Objekt suchen..." tip="Objekt – Objekt, zu dem der Vertrag gehört"
            wert={selectedObjekt ? selectedObjekt.bezeichnung : objektSearch} onChange={(e) => setObjektSearch(e.target.value)}
            offen mindestLaenge={2} festNachAuswahl ausgewaehlt={!!selectedObjekt} onSchliessen={() => setObjektResults([])}
            onEntfernen={() => { setSelectedObjekt(null); setObjektId(''); setHausId(''); }}
            treffer={selectedObjekt ? [] : objektResults.map((o) => ({ id: o.id, label: o.bezeichnung, unter: `${o.strasse} ${o.hausnummer}`, icon: Buildings, iconFlaeche: ZEILEN_ICON.gold.flaeche, iconFarbe: ZEILEN_ICON.gold.farbe }))}
            onWaehlen={(id) => { const o = objektResults.find((x) => x.id === id); if (o) { setObjektId(o.id); setSelectedObjekt(o); setObjektSearch(''); setObjektResults([]); setHausId(''); } }} />
          {/* Haus — nur bei Liegenschaften mit mehreren Häusern */}
          {haeuser.length > 0 && (
            <FormAuswahl label="Haus" wert={hausId} onChange={setHausId} tip="Haus – Nur bei Liegenschaften mit mehreren Häusern"
              optionen={[
                { wert: '', label: 'Ohne Haus (ganze Liegenschaft)', icon: Minus, iconFlaeche: ZEILEN_ICON.leer.flaeche, iconFarbe: ZEILEN_ICON.leer.farbe },
                ...haeuser.map(h => ({ wert: h.id, label: hausAdresse(h) ? `${h.bezeichnung} · ${hausAdresse(h)}` : h.bezeichnung, icon: HouseLine, iconFlaeche: ZEILEN_ICON.gold.flaeche, iconFarbe: ZEILEN_ICON.gold.farbe })),
              ]} />
          )}
          <FormFeld label="Einheit / Wohnung" value={einheit} onChange={(e) => setEinheit(e.target.value)} placeholder="z.B. EG links, Stellplatz 3" />
          <FormSuche label="Mieter" platzhalter="Mieter suchen..." tip="Mieter – Ab 2 Zeichen wird gesucht"
            wert={selectedMieter ? `${selectedMieter.vorname} ${selectedMieter.nachname}` : mieterSearch} onChange={(e) => setMieterSearch(e.target.value)}
            offen mindestLaenge={2} festNachAuswahl ausgewaehlt={!!selectedMieter} onSchliessen={() => setMieterResults([])}
            onEntfernen={() => { setSelectedMieter(null); setMieterId(''); }}
            treffer={selectedMieter ? [] : mieterResults.map((m) => ({ id: m.id, label: `${m.vorname} ${m.nachname}`, icon: User, ...neutral }))}
            onWaehlen={(id) => { const m = mieterResults.find((x) => x.id === id); if (m) { setMieterId(m.id); setSelectedMieter(m); setMieterSearch(''); setMieterResults([]); } }} />
        </FormAbschnitt>

        <FormAbschnitt titel="Laufzeit" icon={CalendarBlank} farbe="orange" pflicht={1} index={2}>
          <FormZeile>
            <FormFeld label="Beginn" typ="date" value={beginn} onChange={(e) => setBeginn(e.target.value)} pflicht />
            {!unbefristet && <FormFeld label="Ende" typ="date" value={ende} onChange={(e) => setEnde(e.target.value)} />}
          </FormZeile>
          <FormSchalter text="Unbefristet" an={unbefristet} onChange={setUnbefristet} icon={PhInfinity} verlauf={ABSCHNITT_FARBE.blau.verlauf} />
          <FormFeld label="Kündigungsfrist" typ="number" value={kuendigungsfristMonate} min={0} max={24} suffix="Mon."
            tip="Kündigungsfrist – 0 bis 24 Monate" onChange={(e) => setKuendigungsfristMonate(parseInt(e.target.value) || 0)} />
        </FormAbschnitt>

        <FormAbschnitt titel="Finanzen" icon={CurrencyEur} farbe="gruen" index={3}>
          <FormZeile>
            <FormFeld label="Kaltmiete" typ="number" step="0.01" placeholder="0.00" value={kaltmiete} onChange={(e) => setKaltmiete(e.target.value)} />
            <FormFeld label="Nebenkosten" typ="number" step="0.01" placeholder="0.00" value={nebenkosten} onChange={(e) => setNebenkosten(e.target.value)} />
            <FormFeld label="Kaution" typ="number" step="0.01" placeholder="0.00" value={kautionHoehe} onChange={(e) => setKautionHoehe(e.target.value)} />
          </FormZeile>
          <FormSegment label="Kaution Status" wert={kautionStatus} onChange={setKautionStatus} optionen={[
            { wert: 'offen', label: 'Offen' }, { wert: 'hinterlegt', label: 'Hinterlegt' }, { wert: 'zurueckgezahlt', label: 'Zurückgezahlt' },
          ]} />
          <FormAuswahl label="Zahlungsweise" wert={zahlungsweise} onChange={setZahlungsweise} optionen={[
            { wert: 'monatlich', label: 'Monatlich' }, { wert: 'quartalsweise', label: 'Quartalsweise' },
            { wert: 'halbjaehrlich', label: 'Halbjährlich' }, { wert: 'jaehrlich', label: 'Jährlich' },
          ].map(o => ({ ...o, icon: Repeat, ...neutral }))} />
          <FormSegment label="Mietanpassung" wert={mietanpassungTyp} onChange={setMietanpassungTyp} optionen={[
            { wert: 'keine', label: 'Keine' }, { wert: 'staffel', label: 'Staffelmiete' }, { wert: 'index', label: 'Indexmiete' },
          ]} />
        </FormAbschnitt>

        <FormAbschnitt titel="Notizen" icon={NotePencil} farbe="violett" index={4}>
          <FormFeld label="Notizen" value={notizen} onChange={(e) => setNotizen(e.target.value)} zeilen={3} placeholder="Optionale Notizen..." />
        </FormAbschnitt>
      </FormRahmen>
    );
  }

  // immo: nackter Inhalt im neuen Design (Mieter-/Immobilienbereich).
  if (immo) return inhalt;

  return (
    <div className="fixed inset-0 z-[250] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" data-vertrag-modal>
      <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#0f172a] to-[#1e293b] p-6 text-white shrink-0">
          <div className="flex justify-between items-center">
            <div className="space-y-1">
              <h2 className="text-lg font-black uppercase tracking-tight">{isEdit ? 'Vertrag bearbeiten' : 'Neuer Vertrag'}</h2>
              <p className="text-[9px] font-black text-white/50 uppercase tracking-[0.2em]">
                {isEdit ? 'Änderungen vornehmen' : 'Vertrag erstellen'}
              </p>
            </div>
            <button onClick={onClose} className="p-2 rounded-xl hover:bg-white/10 transition-colors">
              <X size={18} />
            </button>
          </div>
        </div>

        {inhalt}

        {/* Footer */}
        <div className="p-6 border-t border-gray-100 flex items-center justify-between shrink-0">
          <button onClick={onClose} className="px-5 py-3 text-[10px] font-black text-gray-400 uppercase tracking-widest hover:text-gray-600 transition-colors">
            Abbrechen
          </button>
          <button onClick={handleSave} disabled={saving || !vertragsnummer.trim() || !beginn}
            className="flex items-center gap-2 px-6 py-3 bg-[#0f172a] text-white rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg hover:bg-black transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {isEdit ? 'Speichern' : 'Erstellen'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VertragFormular;
